/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {InputEl} from 'common/components/input';
import {ItemsMenuEl} from 'common/components/items-menu';
import {CheckboxListEl} from 'common/widgets/checkbox-list';
import {ContextPanelEl} from 'common/widgets/context-panel';

import {CodeTipEl} from '../components/code-tip';
import type {DesignSystem} from '../index';
import {Section, SubSection} from './utils';

export function ItemListsSection({designSystem}: {designSystem: DesignSystem}) {
  return (
    <Section title='Item Lists'>
      <SubSection title='mp-checkbox-list' autoDoc={true}>
        <p sel='.desc'>
          For displaying a scrollable, searchable, and keyboard navigable checkbox list of items, with the ability to
          select/deselect all items.
        </p>

        <CodeTipEl sel='.example'>
          <CheckboxListEl
            attrs={{
              items: JSON.stringify(designSystem.helpers.getCheckboxListItems()),
              progressive: true,
              'show-select-all': true,
              'num-columns': 3,
            }}
          />
        </CodeTipEl>

        <p sel='.desc'>With multi-select CSV-text matching</p>

        <CodeTipEl sel='.example'>
          <InputEl
            attrs={{icon: `search`, placeholder: `Search items`}}
            on={{input: ({target}) => designSystem.update({checkboxListSearchFilter: target.value})}}
          />
          <CheckboxListEl
            style={{
              height: `240px`,
            }}
            attrs={{
              items: designSystem.state.checkboxListShowMatchesItems,
              progressive: true,
              'show-select-all': true,
              'show-csv-matches': true,
              'search-filter': designSystem.state.checkboxListSearchFilter,
              'num-columns': 1,
            }}
            on={{
              clickItem: designSystem.helpers.handleCheckboxListShowMatchesClick,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-items-menu' autoDoc={true}>
        <p sel='.desc'>For displaying a scrollable, searchable, and keyboard navigable list of items.</p>

        <p>Helper mp-input search. Keyboard accessibility provided via `keydownProxyEl` prop</p>
        <CodeTipEl sel='.example'>
          <InputEl
            attrs={{icon: `search`, placeholder: `Search items`}}
            on={{input: ({target}) => designSystem.update({itemsMenuSearchFilter: target.value})}}
            hook={{insert: ({elm}) => designSystem.updateElRef({itemsMenuSearch: elm})}}
          />
        </CodeTipEl>

        <p>Example also includes 'mp-context-panel' to show context for hovered items</p>
        <CodeTipEl sel='.example'>
          <ItemsMenuEl
            attrs={{
              'search-filter': designSystem.state.itemsMenuSearchFilter,
              sections: designSystem.state.itemsMenuSections,
            }}
            props={{keydownProxyEl: designSystem.elRefs.itemsMenuSearch}}
            on={{focusItem: designSystem.helpers.populateTooltip}}
          />
          <ContextPanelEl
            attrs={{
              'tooltip-data': designSystem.state.tooltipData,
              'position-parent-selector': `.example`,
              open: !!designSystem.state.tooltipData,
            }}
          />
        </CodeTipEl>

        <p>Example of a read only section item</p>
        <CodeTipEl sel='.example'>
          <ItemsMenuEl
            attrs={{
              'search-filter': designSystem.state.itemsMenuSearchFilter,
              'search-results-already-filtered': true,
              sections: designSystem.state.withReadOnlyItemMenuSections,
            }}
            props={{keydownProxyEl: designSystem.elRefs.itemsMenuSearch}}
            on={{focusItem: designSystem.helpers.populateTooltip}}
          />
        </CodeTipEl>

        <p>Example of items with custom renderers</p>
        <CodeTipEl sel='.example'>
          <ItemsMenuEl
            attrs={{
              'search-filter': designSystem.state.itemsMenuSearchFilter,
              'search-results-already-filtered': true,
            }}
            props={{
              sections: designSystem.state.sectionsWithCustomRenderers,
            }}
            on={{focusItem: designSystem.helpers.populateTooltip}}
          />
        </CodeTipEl>

        <p>Example of items where the action renderer shows up on item hover</p>
        <CodeTipEl sel='.example'>
          <ItemsMenuEl
            attrs={{
              'search-filter': designSystem.state.itemsMenuSearchFilter,
            }}
            props={{
              sections: designSystem.state.sectionsWithHoverActionRenderers,
            }}
            on={{focusItem: designSystem.helpers.populateTooltip}}
          />
        </CodeTipEl>
      </SubSection>
    </Section>
  );
}
